import Link from 'redux-first-router-link'
import { createSelector } from 'reselect'
import createCachedSelector from 're-reselect'
import { emptyObj } from 'util/objects'
import { emptyArr } from 'util/arrays'
import { CHAT_PAGE } from 'subapps/chat/pages'
import { selectRoomFromUrl } from 'ducks/chat/selectors/rooms'
import { selectFoldersById } from 'ducks/folders/selectors/folders'
import selectFolderItems from 'ducks/folders/selectors/items/selectItems'
import { matchFilter } from 'ducks/searches/utils/diff'
import selectCurrentConversationId from './selectCurrentConversationId'
import { queryIdToQuery, removeKeysFromQueryId } from '../utils/query'

export { selectCurrentConversationId }

function selectBase(state) {
  return state.searchV2 || emptyObj
}

export const selectByQueryId = createSelector(
  selectBase,
  base => base.byQueryId
)

export const selectCurrentQueryId = createSelector(
  selectBase,
  base => base.currentQueryId
)

export function selectLastUpdatedAt(state) {
  return selectBase(state).lastUpdatedAt
}

export function selectQueryIdFromProps(_, props) {
  return props.queryId || null
}

export const selectCurrentSearchQuery = createSelector(
  selectByQueryId,
  selectCurrentQueryId,
  (byQueryId, queryId) => {
    return byQueryId[queryId] || emptyObj
  }
)

export const selectCurrentSearchQueryTotalCount = createSelector(
  selectCurrentSearchQuery,
  currentQuery => {
    return currentQuery.conversationCount || 0
  }
)

export const selectCurrentSearchQueryFilter = createSelector(
  selectCurrentSearchQuery,
  selectCurrentQueryId,
  currentQuery => currentQuery.filter || emptyObj
)

export const selectCurrentSearchQueryIsLoading = createSelector(
  selectCurrentSearchQuery,
  currentQuery => !!currentQuery.loading
)

export const selectCurrentSearchQueryIsLoaded = createSelector(
  selectCurrentSearchQuery,
  currentQuery => !!currentQuery.loaded
)

export const selectCurrentSearchPageCursor = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.currentPageCursor || null
)

export const selectNextSearchPageCursor = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.nextPageCursor || null
)

export const selectCurrentSearchHasMore = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.hasAllPages !== true
)

export const selectCurrentSearchConversationIds = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.conversationIds || emptyArr
)

export const selectCurrentSearchQueryIsLoadingMore = createSelector(
  selectCurrentSearchQueryIsLoading,
  selectCurrentSearchQueryIsLoaded,
  (isLoading, isLoaded) => isLoading && isLoaded
)

export const selectCurrentSearchIsError = createSelector(
  selectByQueryId,
  selectCurrentQueryId,
  (byQueryId, queryId) => !!(byQueryId[queryId] || emptyObj).error
)

export function makeSelectConversationCountByQueryId() {
  return createSelector(
    selectByQueryId,
    selectQueryIdFromProps,
    (searchesByQueryId, queryId) => {
      const { conversationCount = 0 } = searchesByQueryId[queryId] || emptyObj
      return conversationCount
    }
  )
}

export const selectConversationUnreadCountByQueryId = createCachedSelector(
  selectByQueryId,
  (_state, queryId) => queryId,
  selectRoomFromUrl,
  selectFoldersById,
  (searchesByQueryId, queryId, roomFromUrl, foldersById) => {
    if (!queryId.includes('folder:')) return null

    const unreadQueryId = queryId.replace('folder:', 'folderunread:')
    const { conversationCount = 0 } =
      searchesByQueryId[unreadQueryId] || emptyObj
    const isRoomOpen = !!roomFromUrl.id

    // While a conversation is open, we want to always regard is as "read"
    if (
      conversationCount > 0 &&
      isRoomOpen &&
      !roomFromUrl.isRead &&
      !roomFromUrl.ignoreNextClose
    ) {
      const { folderId } = queryIdToQuery(queryId)
      const folder = foldersById[folderId]
      const isInFolder = matchFilter(folder, roomFromUrl)
      if (isInFolder) return conversationCount - 1
    }

    return conversationCount
  }
)((_state, queryId) => queryId)

export const selectIsUnread = createSelector(
  selectFolderItems,
  selectRoomFromUrl,
  selectCurrentQueryId,
  selectByQueryId,
  selectFoldersById,
  (
    folderItems,
    roomFromUrl,
    currentQueryId,
    searchesByQueryId,
    foldersById
  ) => {
    if (!currentQueryId) return false
    const channelLessQueryId = removeKeysFromQueryId(
      ['channel'],
      currentQueryId
    )
    return folderItems
      .filter(f => f.displayUnreadIndicator)
      .some(({ queryId }) => {
        const unreadQueryId = queryId.replace('folder:', 'folderunread:')
        let { conversationCount = 0 } =
          searchesByQueryId[unreadQueryId] || emptyObj

        const isRoomOpen = !!roomFromUrl.id

        if (
          conversationCount > 0 &&
          isRoomOpen &&
          !roomFromUrl.isRead &&
          !roomFromUrl.ignoreNextClose &&
          queryId.includes(channelLessQueryId)
        ) {
          const { folderId } = queryIdToQuery(queryId)
          const folder = foldersById[folderId]
          const isInFolder = matchFilter(folder, roomFromUrl)
          if (isInFolder) conversationCount -= 1
        }
        return conversationCount > 0
      })
  }
)

export const selectSortKeyForAgent = () => 'LAST_USER_OR_AGENT_MESSAGE_AT'

const hackAgentSortOptions = [
  {
    id: 'newest',
    name: 'Newest',
    orderBy: 'LAST_USER_OR_AGENT_MESSAGE_AT_DESC',
  },
  {
    id: 'oldest',
    name: 'Oldest',
    orderBy: 'LAST_USER_OR_AGENT_MESSAGE_AT_ASC',
  },
  {
    id: 'longestWaiting',
    name: 'Waiting longest',
    orderBy: 'LAST_UNANSWERED_USER_MESSAGE_AT_ASC',
  },
]

export const selectAgentSortOptions = () => hackAgentSortOptions

export const selectSortOptions = createSelector(
  selectCurrentQueryId,
  selectAgentSortOptions,
  (currentQueryId, agentSortOptions) => {
    const { orderBy: currentOrderBy } = queryIdToQuery(currentQueryId) || {}
    return agentSortOptions.map(({ id, name, orderBy }) => ({
      active: currentOrderBy === orderBy,
      as: Link,
      key: id,
      text: name,
      to: {
        type: CHAT_PAGE,
        payload: {
          query: queryIdToQuery(`${currentQueryId} orderBy:${orderBy}`),
        },
      },
      value: id,
    }))
  }
)

export const selectCurrentSearchNextConversationId = createSelector(
  selectCurrentSearchConversationIds,
  selectCurrentConversationId,
  (ids, currentConversationId) => {
    if (!ids) return null
    if (!currentConversationId) return null
    const currentTicketIndex = ids.indexOf(currentConversationId)
    return ids[currentTicketIndex + 1]
  }
)

export const selectCurrentSearchPreviousConversationId = createSelector(
  selectCurrentSearchConversationIds,
  selectCurrentConversationId,
  (ids, currentConversationId) => {
    if (!ids) return null
    if (!currentConversationId) return null
    const currentTicketIndex = ids.indexOf(currentConversationId)
    return ids[currentTicketIndex - 1]
  }
)
